/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import { useState } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import { setPlayerTeam, addPriority } from 'redux/slices';
import { useSelector, useDispatch } from 'react-redux';

const getPlayerPoints = (p) => p.totalPoints ?? (p.wins - p.losses);

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const getCombinations = (array, size) => {
  const result = [];
  const combine = (start, combo) => {
    if (combo.length === size) {
      result.push([...combo]);
      return;
    }
    for (let i = start; i < array.length; i += 1) {
      combo.push(array[i]);
      combine(i + 1, combo);
      combo.pop();
    }
  };
  combine(0, []);
  return result;
};

const findBestCombination = (candidates, slotsNeeded, baseScore, targetScore) => {
  if (candidates.length <= slotsNeeded) {
    return candidates;
  }
  const combinations = getCombinations(candidates, slotsNeeded);
  let bestCombo = combinations[0];
  let bestDiff = Infinity;
  combinations.forEach((combo) => {
    const comboScore = combo.reduce((sum, p) => sum + getPlayerPoints(p), 0);
    const totalScore = baseScore + comboScore;
    const diff = Math.abs(totalScore - targetScore);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestCombo = combo;
    }
  });
  return bestCombo;
};

export function GenerateBalancedTeamBtn({ label = 'Próximos jogadores' } = {}) {
  const players = useSelector((state) => state.players);
  const playersPerTeam = useSelector((state) => state.game.playersPerTeam);
  const genderBalanceMode = useSelector((state) => state.game.genderBalanceMode);
  const genderRestrictedMode = useSelector((state) => state.game.genderRestrictedMode);
  const femalesPerTeam = useSelector((state) => state.game.femalesPerTeam);
  const setterRestrictedMode = useSelector((state) => state.game.setterRestrictedMode);
  const autoBalanceMode = useSelector((state) => state.game.autoBalanceMode);
  const nextPlayers = players.filter((p) => p.teamId === 4);
  const dispatch = useDispatch();

  const [adjustmentModal, setAdjustmentModal] = useState(null);

  const generateTeam = () => {
    const teamAPlayers = players.filter((p) => p.teamId === 1);
    const teamBPlayers = players.filter((p) => p.teamId === 2);
    const teamToFill = teamAPlayers.length < teamBPlayers.length ? 1 : 2;

    const targetTeamId = teamAPlayers.length === teamBPlayers.length ? teamToFill : (teamAPlayers.length < teamBPlayers.length ? 1 : 2);

    if (nextPlayers.length === playersPerTeam) {
      nextPlayers.forEach((player) => {
        dispatch(setPlayerTeam({ playerId: player.playerId, teamId: targetTeamId }));
      });
      return;
    }

    const currentTargetTeam = targetTeamId === 1 ? teamAPlayers : teamBPlayers;
    const opposingTeam = targetTeamId === 1 ? teamBPlayers : teamAPlayers;
    const opposingTeamScore = opposingTeam.reduce((acc, p) => acc + getPlayerPoints(p), 0);

    const playersMissing = playersPerTeam - currentTargetTeam.length;
    const playersWaiting = players.filter((p) => p.teamId === 0 && p.checkedIn);

    const currentFemaleCount = currentTargetTeam.filter((p) => p.gender === 'F').length;

    let femalesNeeded = 0;
    if (genderBalanceMode) {
      femalesNeeded = Math.max(0, femalesPerTeam - currentFemaleCount);
    }

    const groupedByWaiting = playersWaiting
      .sort((a, b) => b.waitingFor - a.waitingFor)
      .reduce((acc, player) => {
        if (acc.length === 0 || acc[acc.length - 1][0].waitingFor !== player.waitingFor) {
          acc.push([player]);
        } else {
          acc[acc.length - 1].push(player);
        }
        return acc;
      }, []);

    const initialTeamScore = currentTargetTeam.reduce((acc, p) => acc + getPlayerPoints(p), 0);
    const targetTeamHasSetter = currentTargetTeam.some((p) => p.playerType === 'Setter');
    const opposingTeamHasSetter = opposingTeam.some((p) => p.playerType === 'Setter');
    const needSetterForRestricted = setterRestrictedMode && opposingTeamHasSetter && !targetTeamHasSetter;

    // Se precisamos de mulheres, dar prioridade para elas (mesmo furando fila)
    // No modo restrito, respeitar o limite de femalesPerTeam
    // Se precisamos de setter restrito e há uma levantadora mulher, ela deve ser priorizada
    let priorityFemales = [];
    const maxFemalesAllowed = genderRestrictedMode ? Math.max(0, femalesPerTeam - currentFemaleCount) : femalesNeeded;

    // Verificar se há levantadora mulher disponível quando precisamos de setter restrito
    const femaleSetterForRestricted = needSetterForRestricted
      ? playersWaiting.find((p) => p.playerType === 'Setter' && p.gender === 'F')
      : null;

    if (genderBalanceMode && femalesNeeded > 0 && maxFemalesAllowed > 0) {
      const allWaitingFemales = playersWaiting.filter((p) => p.gender === 'F');
      if (allWaitingFemales.length > 0) {
        // Se precisamos de setter restrito e há levantadora mulher, ela deve ser a primeira prioridade
        // pois ela conta tanto como setter quanto como mulher
        let sortedFemales;
        if (femaleSetterForRestricted) {
          const otherFemales = allWaitingFemales.filter((p) => p.playerId !== femaleSetterForRestricted.playerId);
          // Ordenar as outras mulheres normalmente
          const sortedOtherFemales = [...otherFemales].sort((a, b) => {
            if (b.waitingFor !== a.waitingFor) return b.waitingFor - a.waitingFor;
            if (a.stayIn && !b.stayIn) return -1;
            if (!a.stayIn && b.stayIn) return 1;
            if (autoBalanceMode) {
              const scoreWithA = initialTeamScore + getPlayerPoints(a);
              const scoreWithB = initialTeamScore + getPlayerPoints(b);
              return Math.abs(scoreWithA - opposingTeamScore) - Math.abs(scoreWithB - opposingTeamScore);
            }
            return 0;
          });
          // Levantadora mulher primeiro, depois as outras
          sortedFemales = [femaleSetterForRestricted, ...sortedOtherFemales];
        } else {
          // Ordenar por waitingFor, depois stayIn tem prioridade dentro do mesmo waitingFor
          sortedFemales = [...allWaitingFemales].sort((a, b) => {
            if (b.waitingFor !== a.waitingFor) return b.waitingFor - a.waitingFor;
            // Mesmo waitingFor: stayIn tem prioridade
            if (a.stayIn && !b.stayIn) return -1;
            if (!a.stayIn && b.stayIn) return 1;
            // Se mesmo waitingFor e mesmo stayIn: balancear por pontos OU aleatório
            if (autoBalanceMode) {
              const scoreWithA = initialTeamScore + getPlayerPoints(a);
              const scoreWithB = initialTeamScore + getPlayerPoints(b);
              return Math.abs(scoreWithA - opposingTeamScore) - Math.abs(scoreWithB - opposingTeamScore);
            }
            return 0; // Manter ordem (será embaralhado depois)
          });
        }
        // Se não é autoBalance, embaralhar grupos com mesmo waitingFor (mas stayIn primeiro)
        // Mas manter levantadora mulher na frente se ela foi priorizada
        let finalFemales = sortedFemales;
        if (!autoBalanceMode && !femaleSetterForRestricted) {
          const groupedFemales = sortedFemales.reduce((acc, f) => {
            if (acc.length === 0 || acc[acc.length - 1][0].waitingFor !== f.waitingFor) {
              acc.push([f]);
            } else {
              acc[acc.length - 1].push(f);
            }
            return acc;
          }, []);
          // Dentro de cada grupo: stayIn primeiro, depois embaralha os outros
          finalFemales = groupedFemales.flatMap((g) => {
            const stayInFemales = g.filter((f) => f.stayIn);
            const nonStayInFemales = g.filter((f) => !f.stayIn);
            return [...stayInFemales, ...shuffleArray(nonStayInFemales)];
          });
        } else if (!autoBalanceMode && femaleSetterForRestricted) {
          // Manter levantadora na frente, embaralhar o resto
          const otherFemales = sortedFemales.slice(1);
          const groupedOthers = otherFemales.reduce((acc, f) => {
            if (acc.length === 0 || acc[acc.length - 1][0].waitingFor !== f.waitingFor) {
              acc.push([f]);
            } else {
              acc[acc.length - 1].push(f);
            }
            return acc;
          }, []);
          const shuffledOthers = groupedOthers.flatMap((g) => {
            const stayInFemales = g.filter((f) => f.stayIn);
            const nonStayInFemales = g.filter((f) => !f.stayIn);
            return [...stayInFemales, ...shuffleArray(nonStayInFemales)];
          });
          finalFemales = [femaleSetterForRestricted, ...shuffledOthers];
        }
        // Pegar apenas a quantidade necessária de mulheres, respeitando o limite
        priorityFemales = finalFemales.slice(0, Math.min(femalesNeeded, maxFemalesAllowed));
      }
    }

    // Começar com as mulheres prioritárias já selecionadas
    const initialSelectedPlayers = priorityFemales.slice(0, Math.min(priorityFemales.length, playersMissing));
    const initialFemalesNeeded = Math.max(0, femalesNeeded - initialSelectedPlayers.length);
    const slotsForNonPriority = playersMissing - initialSelectedPlayers.length;

    // Calcular quantas mulheres ainda podem entrar considerando a restrição
    const femalesInInitial = initialSelectedPlayers.filter((p) => p.gender === 'F').length;
    const initialFemalesAllowed = genderRestrictedMode
      ? Math.max(0, femalesPerTeam - currentFemaleCount - femalesInInitial)
      : Infinity;

    // Selecionar jogadores por grupo de waitingFor
    // stayIn tem prioridade DENTRO do mesmo grupo, não fura fila de quem espera mais
    const selectionState = groupedByWaiting.reduce((state, group) => {
      if (state.selected.length >= slotsForNonPriority) return state;

      // Filtrar jogadores que já foram selecionados como prioridade (mulheres)
      const availableInGroup = group.filter(
        (p) => !initialSelectedPlayers.some((sp) => sp.playerId === p.playerId),
      );

      const slotsLeft = slotsForNonPriority - state.selected.length;

      // Separar stayIn e não-stayIn dentro do grupo
      const stayInInGroup = availableInGroup.filter((p) => p.stayIn);
      const nonStayInInGroup = availableInGroup.filter((p) => !p.stayIn);

      // Primeiro adicionar stayIn (respeitando restrição de gênero)
      const stayInResult = stayInInGroup.reduce((acc, p) => {
        if (acc.added.length >= slotsLeft) return acc;
        if (p.gender === 'F' && genderRestrictedMode && acc.femalesLeft <= 0) {
          return acc; // Não pode adicionar mais mulheres
        }
        return {
          added: [...acc.added, p],
          femalesLeft: p.gender === 'F' ? acc.femalesLeft - 1 : acc.femalesLeft,
        };
      }, { added: [], femalesLeft: state.femalesAllowed });

      const slotsAfterStayIn = slotsLeft - stayInResult.added.length;

      // Depois adicionar não-stayIn (respeitando restrição de gênero)
      let nonStayInToAdd;
      if (autoBalanceMode) {
        const currentBaseScore = initialTeamScore
          + initialSelectedPlayers.reduce((sum, p) => sum + getPlayerPoints(p), 0)
          + state.selected.reduce((sum, p) => sum + getPlayerPoints(p), 0)
          + stayInResult.added.reduce((sum, p) => sum + getPlayerPoints(p), 0);
        nonStayInToAdd = findBestCombination(nonStayInInGroup, slotsAfterStayIn, currentBaseScore, opposingTeamScore);
      } else {
        nonStayInToAdd = shuffleArray(nonStayInInGroup).slice(0, slotsAfterStayIn);
      }

      // Filtrar mulheres extras se necessário
      const nonStayInFiltered = genderRestrictedMode
        ? nonStayInToAdd.reduce((acc, p) => {
          if (p.gender === 'F' && acc.femalesLeft <= 0) return acc;
          return {
            added: [...acc.added, p],
            femalesLeft: p.gender === 'F' ? acc.femalesLeft - 1 : acc.femalesLeft,
          };
        }, { added: [], femalesLeft: stayInResult.femalesLeft })
        : { added: nonStayInToAdd, femalesLeft: stayInResult.femalesLeft };

      return {
        selected: [...state.selected, ...stayInResult.added, ...nonStayInFiltered.added],
        femalesAllowed: nonStayInFiltered.femalesLeft,
      };
    }, { selected: [], femalesAllowed: initialFemalesAllowed });

    const selectedFromWaiting = selectionState.selected;

    // Jogadores que vão ceder lugar (para marcar com P)
    // Quem entraria por ordem PURA da fila (top playersMissing), ignorando a prioridade de mulher.
    // Assim, o waiter empurrado pra fora por uma mulher que furou fila recebe a compensação (+10).
    const playersWhoWouldEnterNormally = groupedByWaiting.reduce((acc, group) => {
      if (acc.length >= playersMissing) return acc;
      return [...acc, ...group.slice(0, playersMissing - acc.length)];
    }, []);
    const playersGivingUpSpot = playersWhoWouldEnterNormally.filter(
      (p) => !selectedFromWaiting.some((s) => s.playerId === p.playerId)
        && !initialSelectedPlayers.some((s) => s.playerId === p.playerId),
    );

    // Resultado final: mulheres prioritárias + jogadores selecionados da fila
    const allSelectedPlayers = [...initialSelectedPlayers, ...selectedFromWaiting];
    let result = {
      selectedPlayers: allSelectedPlayers,
      currentScore: allSelectedPlayers.reduce((sum, p) => sum + getPlayerPoints(p), 0),
      femalesNeeded: initialFemalesNeeded,
    };

    // Se ainda faltam jogadores (caso raro), continuar preenchendo
    if (result.selectedPlayers.length < playersMissing) {
      const alreadySelected = result.selectedPlayers.map((p) => p.playerId);
      const currentFemalesInResult = result.selectedPlayers.filter((p) => p.gender === 'F').length;
      const femalesStillAllowed = genderRestrictedMode
        ? Math.max(0, femalesPerTeam - currentFemaleCount - currentFemalesInResult)
        : Infinity;

      let remainingPlayers = playersWaiting
        .filter((p) => !alreadySelected.includes(p.playerId))
        .sort((a, b) => b.waitingFor - a.waitingFor);

      // No modo restrito, filtrar mulheres se já atingimos o limite
      if (genderRestrictedMode && femalesStillAllowed === 0) {
        remainingPlayers = remainingPlayers.filter((p) => p.gender !== 'F');
      }

      const slotsStillNeeded = playersMissing - result.selectedPlayers.length;
      const additionalPlayers = remainingPlayers.slice(0, slotsStillNeeded);
      result.selectedPlayers = [...result.selectedPlayers, ...additionalPlayers];
    }

    // Garantir que não exceda playersMissing
    if (result.selectedPlayers.length > playersMissing) {
      result.selectedPlayers = result.selectedPlayers.slice(0, playersMissing);
    }

    // Verificar se precisa de setter restrito
    const selectedHasSetter = result.selectedPlayers.some((p) => p.playerType === 'Setter');
    if (needSetterForRestricted && !selectedHasSetter) {
      const allWaitingSetters = playersWaiting.filter((p) => p.playerType === 'Setter' && !result.selectedPlayers.includes(p));
      if (allWaitingSetters.length > 0) {
        let setterToAdd;
        if (autoBalanceMode) {
          const sortedSetters = [...allWaitingSetters].sort((a, b) => {
            const scoreWithA = result.currentScore + getPlayerPoints(a);
            const scoreWithB = result.currentScore + getPlayerPoints(b);
            return Math.abs(scoreWithA - opposingTeamScore) - Math.abs(scoreWithB - opposingTeamScore);
          });
          [setterToAdd] = sortedSetters;
        } else {
          // Modo sem balanceamento: escolher aleatório
          const shuffledSetters = shuffleArray(allWaitingSetters);
          [setterToAdd] = shuffledSetters;
        }
        const nonSettersSelected = result.selectedPlayers.filter((p) => p.playerType !== 'Setter');

        // Se genderRestrictedMode está ativo, precisamos considerar o gênero do setter
        // ao decidir quem remover para dar lugar ao setter
        let nonSettersToKeep;
        let setterDisplacedPlayer;

        if (genderBalanceMode && genderRestrictedMode) {
          const setterIsFemale = setterToAdd.gender === 'F';
          const currentFemalesInSelected = nonSettersSelected.filter((p) => p.gender === 'F').length;
          const femalesInTeamAlready = currentTargetTeam.filter((p) => p.gender === 'F').length;
          const totalFemalesWithSetter = femalesInTeamAlready + currentFemalesInSelected + (setterIsFemale ? 1 : 0);

          if (setterIsFemale && totalFemalesWithSetter > femalesPerTeam) {
            // Levantadora é mulher e excederia o limite: remover uma mulher não-setter
            const femalesInSelected = nonSettersSelected.filter((p) => p.gender === 'F');

            if (femalesInSelected.length > 0) {
              // Remover a última mulher adicionada para dar lugar à levantadora
              setterDisplacedPlayer = femalesInSelected[femalesInSelected.length - 1];
              nonSettersToKeep = nonSettersSelected.filter((p) => p.playerId !== setterDisplacedPlayer.playerId).slice(0, playersMissing - 1);
            } else {
              // Não há mulheres para remover, remover qualquer um
              nonSettersToKeep = nonSettersSelected.slice(0, playersMissing - 1);
              setterDisplacedPlayer = nonSettersSelected[playersMissing - 1];
            }
          } else if (!setterIsFemale && currentFemalesInSelected < (femalesPerTeam - femalesInTeamAlready)) {
            // Levantador é homem e ainda precisamos de mulheres: remover um homem
            const malesInSelected = nonSettersSelected.filter((p) => p.gender !== 'F');

            if (malesInSelected.length > 0) {
              // Remover o último homem adicionado para dar lugar ao levantador
              setterDisplacedPlayer = malesInSelected[malesInSelected.length - 1];
              nonSettersToKeep = nonSettersSelected.filter((p) => p.playerId !== setterDisplacedPlayer.playerId).slice(0, playersMissing - 1);
            } else {
              // Não há homens para remover, remover qualquer um
              nonSettersToKeep = nonSettersSelected.slice(0, playersMissing - 1);
              setterDisplacedPlayer = nonSettersSelected[playersMissing - 1];
            }
          } else {
            // Caso normal: remover o último jogador
            nonSettersToKeep = nonSettersSelected.slice(0, playersMissing - 1);
            setterDisplacedPlayer = nonSettersSelected[playersMissing - 1];
          }
        } else {
          // Sem restrição de gênero: comportamento original
          nonSettersToKeep = nonSettersSelected.slice(0, playersMissing - 1);
          setterDisplacedPlayer = nonSettersSelected[playersMissing - 1];
        }

        // O jogador que cede lugar para o setter também recebe prioridade
        if (setterDisplacedPlayer && !playersGivingUpSpot.includes(setterDisplacedPlayer)) {
          playersGivingUpSpot.push(setterDisplacedPlayer);
        }

        result = {
          selectedPlayers: [setterToAdd, ...nonSettersToKeep],
          currentScore: [setterToAdd, ...nonSettersToKeep].reduce((sum, p) => sum + getPlayerPoints(p), 0),
          femalesNeeded: 0,
        };
      }
    }

    // Verificar ajustes de gênero nos times
    if (genderBalanceMode && genderRestrictedMode) {
      const opposingFemaleCount = opposingTeam.filter((p) => p.gender === 'F').length;
      const femalesMissingInOpposing = femalesPerTeam - opposingFemaleCount;
      const femalesExcessInOpposing = opposingFemaleCount - femalesPerTeam;

      // Caso 1: Time vencedor precisa de mulher
      if (femalesMissingInOpposing > 0) {
        const selectedPlayerIds = result.selectedPlayers.map((p) => p.playerId);
        const femalesInQueue = playersWaiting.filter(
          (p) => p.gender === 'F' && !selectedPlayerIds.includes(p.playerId),
        );

        if (femalesInQueue.length > 0) {
          const opposingMales = opposingTeam.filter((p) => p.gender !== 'F');
          const swappableMales = setterRestrictedMode
            ? opposingMales.filter((p) => p.playerType !== 'Setter')
            : opposingMales;

          if (swappableMales.length > 0) {
            const femalesToAdd = femalesInQueue.slice(0, Math.min(femalesMissingInOpposing, swappableMales.length));
            const malesToMove = swappableMales.slice(0, femalesToAdd.length);

            setAdjustmentModal({
              type: 'addFemaleToWinner',
              selectedPlayers: result.selectedPlayers,
              targetTeamId,
              opposingTeamId: targetTeamId === 1 ? 2 : 1,
              femalesToAdd,
              malesToMove,
            });
            return;
          }
        }
      }

      // Caso 2: Time vencedor tem mulheres a mais (configuração diminuiu)
      if (femalesExcessInOpposing > 0) {
        const opposingFemales = opposingTeam.filter((p) => p.gender === 'F');
        const femalesToMoveFromWinner = opposingFemales.slice(-femalesExcessInOpposing);

        // Homens da fila que vão para o time vencedor (substituir as mulheres que saem)
        const selectedPlayerIds = result.selectedPlayers.map((p) => p.playerId);
        const malesInQueue = playersWaiting.filter(
          (p) => p.gender !== 'F' && !selectedPlayerIds.includes(p.playerId),
        );
        const malesToAddToWinner = malesInQueue.slice(0, femalesToMoveFromWinner.length);

        // Mulheres do time que entra que vão ceder lugar para as mulheres do vencedor
        // (igual homens cedem lugar quando mulher fura fila)
        const targetFemalesInResult = result.selectedPlayers.filter((p) => p.gender === 'F');
        const femalesGivingPriority = targetFemalesInResult.slice(-femalesToMoveFromWinner.length);

        if (femalesToMoveFromWinner.length > 0) {
          setAdjustmentModal({
            type: 'removeFemaleFromWinner',
            selectedPlayers: result.selectedPlayers,
            targetTeamId,
            opposingTeamId: targetTeamId === 1 ? 2 : 1,
            femalesToMove: femalesToMoveFromWinner,
            malesToAddToWinner,
            femalesGivingPriority,
          });
          return;
        }
      }
    }

    result.selectedPlayers.forEach((player) => {
      dispatch(setPlayerTeam({ playerId: player.playerId, teamId: targetTeamId }));
    });

    // Dar prioridade aos jogadores que cederam lugar para mulheres prioritárias
    playersGivingUpSpot.forEach((player) => {
      dispatch(addPriority(player.playerId));
    });
  };

  const handleConfirmAdjustment = () => {
    if (!adjustmentModal) return;

    if (adjustmentModal.type === 'addFemaleToWinner') {
      // Identificar jogadores do time perdedor que vão ceder lugar para os homens do time vencedor
      const malesInSelected = adjustmentModal.selectedPlayers.filter((p) => p.gender !== 'F');
      const femalesToAddCount = adjustmentModal.femalesToAdd?.length || 0;
      const playersToGivePriority = malesInSelected.slice(-femalesToAddCount);

      // Jogadores que vão entrar no time perdedor (excluindo os que cedem lugar)
      const playersToEnter = adjustmentModal.selectedPlayers.filter(
        (p) => !playersToGivePriority.some((pg) => pg.playerId === p.playerId),
      );

      // Adicionar jogadores selecionados ao time perdedor (menos os que cedem lugar)
      playersToEnter.forEach((player) => {
        dispatch(setPlayerTeam({ playerId: player.playerId, teamId: adjustmentModal.targetTeamId }));
      });

      // Mover homens do time vencedor para o time perdedor
      adjustmentModal.malesToMove?.forEach((male) => {
        dispatch(setPlayerTeam({ playerId: male.playerId, teamId: adjustmentModal.targetTeamId }));
      });

      // Adicionar mulheres da fila ao time vencedor
      adjustmentModal.femalesToAdd?.forEach((female) => {
        dispatch(setPlayerTeam({ playerId: female.playerId, teamId: adjustmentModal.opposingTeamId }));
      });

      // Dar prioridade aos jogadores que cederam lugar
      playersToGivePriority.forEach((player) => {
        dispatch(addPriority(player.playerId));
      });
    } else if (adjustmentModal.type === 'removeFemaleFromWinner') {
      // Remover mulheres do time que entra que vão ceder lugar (se houver)
      const femalesGivingPriorityIds = (adjustmentModal.femalesGivingPriority || []).map((p) => p.playerId);
      const playersToEnter = adjustmentModal.selectedPlayers.filter(
        (p) => !femalesGivingPriorityIds.includes(p.playerId),
      );

      // Adicionar jogadores selecionados ao time perdedor (menos as que cedem lugar)
      playersToEnter.forEach((player) => {
        dispatch(setPlayerTeam({ playerId: player.playerId, teamId: adjustmentModal.targetTeamId }));
      });

      // Mover mulheres do time vencedor para o time perdedor
      adjustmentModal.femalesToMove?.forEach((female) => {
        dispatch(setPlayerTeam({ playerId: female.playerId, teamId: adjustmentModal.targetTeamId }));
      });

      // Adicionar homens da fila ao time vencedor
      adjustmentModal.malesToAddToWinner?.forEach((male) => {
        dispatch(setPlayerTeam({ playerId: male.playerId, teamId: adjustmentModal.opposingTeamId }));
      });

      // Dar prioridade às mulheres do time que entra que cederam lugar
      adjustmentModal.femalesGivingPriority?.forEach((female) => {
        dispatch(addPriority(female.playerId));
      });
    }

    setAdjustmentModal(null);
  };

  const handleIgnoreAdjustment = () => {
    if (!adjustmentModal) return;

    // Apenas adicionar jogadores selecionados ao time perdedor, sem ajustes
    adjustmentModal.selectedPlayers.forEach((player) => {
      dispatch(setPlayerTeam({ playerId: player.playerId, teamId: adjustmentModal.targetTeamId }));
    });

    setAdjustmentModal(null);
  };

  return (
    <>
      <Button variant="contained" onClick={() => generateTeam()}>
        {label}
      </Button>

      <Dialog open={!!adjustmentModal} onClose={() => setAdjustmentModal(null)}>
        <DialogTitle>Ajustar times</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {adjustmentModal?.type === 'addFemaleToWinner' && (
              <>
                O Time
                {' '}
                <strong>{adjustmentModal?.opposingTeamId === 1 ? 'A' : 'B'}</strong>
                {' '}
                precisa de
                {' '}
                <strong>{adjustmentModal?.femalesToAdd?.length}</strong>
                {' '}
                mulher(es) para atender a configuração de
                {' '}
                <strong>{femalesPerTeam}</strong>
                {' '}
                mulher(es) por time.
              </>
            )}
            {adjustmentModal?.type === 'removeFemaleFromWinner' && (
              <>
                O Time
                {' '}
                <strong>{adjustmentModal?.opposingTeamId === 1 ? 'A' : 'B'}</strong>
                {' '}
                tem
                {' '}
                <strong>
                  {(adjustmentModal?.femalesToMove?.length || 0) + (adjustmentModal?.femalesToPriority?.length || 0)}
                </strong>
                {' '}
                mulher(es) a mais para a configuração de
                {' '}
                <strong>{femalesPerTeam}</strong>
                {' '}
                mulher(es) por time.
              </>
            )}
            <br />
            <br />
            <strong>Ajuste sugerido:</strong>
            <br />
            {adjustmentModal?.malesToMove?.map((male) => (
              <span key={male.playerId}>
                •
                {' '}
                <strong>{male.playerName}</strong>
                {' '}
                sai do Time
                {' '}
                {adjustmentModal?.opposingTeamId === 1 ? 'A' : 'B'}
                {' '}
                e vai para o Time
                {' '}
                {adjustmentModal?.targetTeamId === 1 ? 'A' : 'B'}
                <br />
              </span>
            ))}
            {adjustmentModal?.femalesToAdd?.map((female) => (
              <span key={female.playerId}>
                •
                {' '}
                <strong>{female.playerName}</strong>
                {' '}
                entra no Time
                {' '}
                {adjustmentModal?.opposingTeamId === 1 ? 'A' : 'B'}
                <br />
              </span>
            ))}
            {adjustmentModal?.femalesToMove?.map((female) => (
              <span key={female.playerId}>
                •
                {' '}
                <strong>{female.playerName}</strong>
                {' '}
                sai do Time
                {' '}
                {adjustmentModal?.opposingTeamId === 1 ? 'A' : 'B'}
                {' '}
                e vai para o Time
                {' '}
                {adjustmentModal?.targetTeamId === 1 ? 'A' : 'B'}
                <br />
              </span>
            ))}
            {adjustmentModal?.malesToAddToWinner?.map((male) => (
              <span key={male.playerId}>
                •
                {' '}
                <strong>{male.playerName}</strong>
                {' '}
                entra no Time
                {' '}
                {adjustmentModal?.opposingTeamId === 1 ? 'A' : 'B'}
                {' '}
                (da fila)
                <br />
              </span>
            ))}
            {adjustmentModal?.femalesGivingPriority?.map((female) => (
              <span key={female.playerId}>
                •
                {' '}
                <strong>{female.playerName}</strong>
                {' '}
                cede lugar e recebe prioridade (P)
                <br />
              </span>
            ))}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleIgnoreAdjustment}>Ignorar</Button>
          <Button variant="contained" onClick={handleConfirmAdjustment}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
